import {
  DeviceClassList,
  DeviceClassRule,
  DeviceClassRuleList,
  DeviceModel,
  DeviceModelList,
  DeviceUnit,
  DeviceUnitList,
  DeviceHistory,
  DeviceUnitAsAt,
  DeviceVersion,
  Installation,
  InstallationList,
  KnownAddressList,
  SubmissionRejectionList,
  type DeviceClassRuleInput,
  type DeviceModelInput,
  type DeviceModelCorrection,
  type DeviceModelStatusChange,
  type DeviceUnitCorrection,
  type DeviceUnitInput,
  type DeviceUnitComplianceChange,
  type DeviceUnitStatusChange,
  type DeviceUnitTransfer,
  type EntityKind,
  type InstallationCorrection,
  type InstallationDecommission,
  type InstallationInput,
} from '@regigov/contracts';
import { api } from './client';

const json = (body: unknown, method = 'POST'): RequestInit => ({ method, body: JSON.stringify(body) });

function qs(params: Record<string, string | number | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export interface ModelFilter {
  q?: string;
  manufacturerId?: string;
  deviceClass?: string;
  status?: string;
  /** Approved models of every manufacturer, not just the caller's own. */
  catalogue?: 'true';
}
export interface UnitFilter {
  q?: string;
  modelId?: string;
  ownerParticipantId?: string;
  deviceClass?: string;
  status?: string;
}
export interface InstallationFilter {
  q?: string;
  unitId?: string;
  installerParticipantId?: string;
  status?: string;
  geocodeStatus?: 'geocoded' | 'not_geocoded' | 'not_recorded';
  limit?: number;
}

export interface AsAt {
  asOf?: string;
  knownAt?: string;
}

export const rulesApi = {
  list: () => api('/devices/rules', DeviceClassRuleList),
  classes: () => api('/devices/rules/classes', DeviceClassList),
  current: (deviceClass: string, on?: string) => api(`/devices/rules/classes/${deviceClass}/current${qs({ on })}`, DeviceClassRule),
  get: (id: number) => api(`/devices/rules/${id}`, DeviceClassRule),
  publish: (input: DeviceClassRuleInput) => api('/devices/rules', DeviceClassRule, json(input)),
};

export const modelsApi = {
  search: (f: ModelFilter) => api(`/devices/models${qs({ ...f })}`, DeviceModelList),
  get: (id: string) => api(`/devices/models/${id}`, DeviceModel),
  register: (input: DeviceModelInput) => api('/devices/models', DeviceModel, json(input)),
  setStatus: (id: string, change: DeviceModelStatusChange) => api(`/devices/models/${id}/status`, DeviceModel, json(change, 'PATCH')),
  history: (id: string) => api(`/devices/models/${id}/history`, DeviceHistory),
  at: (id: string, q: AsAt) => api(`/devices/models/${id}/at${qs({ ...q })}`, DeviceVersion),
  correct: (id: string, body: DeviceModelCorrection) => api(`/devices/models/${id}/corrections`, DeviceModel, json(body)),
};

export const unitsApi = {
  search: (f: UnitFilter) => api(`/devices/units${qs({ ...f })}`, DeviceUnitList),
  get: (id: string) => api(`/devices/units/${id}`, DeviceUnit),
  register: (input: DeviceUnitInput) => api('/devices/units', DeviceUnit, json(input)),
  setStatus: (id: string, change: DeviceUnitStatusChange) => api(`/devices/units/${id}/status`, DeviceUnit, json(change, 'PATCH')),
  setCompliance: (id: string, change: DeviceUnitComplianceChange) => api(`/devices/units/${id}/compliance-status`, DeviceUnit, json(change, 'PATCH')),
  transfer: (id: string, body: DeviceUnitTransfer) => api(`/devices/units/${id}/transfer`, DeviceUnit, json(body)),
  history: (id: string) => api(`/devices/units/${id}/history`, DeviceHistory),
  at: (id: string, q: AsAt) => api(`/devices/units/${id}/at${qs({ ...q })}`, DeviceUnitAsAt),
  correct: (id: string, body: DeviceUnitCorrection) => api(`/devices/units/${id}/corrections`, DeviceUnit, json(body)),
};

export const installationsApi = {
  search: (f: InstallationFilter) => api(`/devices/installations${qs({ ...f })}`, InstallationList),
  get: (id: string) => api(`/devices/installations/${id}`, Installation),
  register: (input: InstallationInput) => api('/devices/installations', Installation, json(input)),
  decommission: (id: string, body: InstallationDecommission) => api(`/devices/installations/${id}/decommission`, Installation, json(body)),
  history: (id: string) => api(`/devices/installations/${id}/history`, DeviceHistory),
  at: (id: string, q: AsAt) => api(`/devices/installations/${id}/at${qs({ ...q })}`, DeviceVersion),
  correct: (id: string, body: InstallationCorrection) => api(`/devices/installations/${id}/corrections`, Installation, json(body)),
};

export const addressesApi = {
  search: (q: string) => api(`/addresses${qs({ q })}`, KnownAddressList),
};

export const rejectionsApi = {
  list: (f: { entityKind?: EntityKind; participantId?: string } = {}) => api(`/devices/rejections${qs({ ...f })}`, SubmissionRejectionList),
};
